import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabase/client';
import { supabaseFreeTable } from '../utils/supabase/clientFreeTable';

export const useEmailTemplates = (restaurantId) => {
  return useQuery({
    queryKey: ['emailTemplates', restaurantId],
    queryFn: async () => {
      if (!restaurantId) {
        throw new Error('Restaurant ID is required');
      }
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, restaurant_id, template_key, language, subject, body, created_at, updated_at')
        .eq('restaurant_id', restaurantId)
        .order('template_key', { ascending: true })
        .order('language', { ascending: true });
      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    enabled: !!restaurantId,
  });
};

export const useEmailTemplate = (restaurantId, templateKey, language) => {
  return useQuery({
    queryKey: ['emailTemplate', restaurantId, templateKey, language],
    queryFn: async () => {
      if (!restaurantId || !templateKey || !language) {
        throw new Error('Restaurant ID, template key, and language are required');
      }
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, restaurant_id, template_key, language, subject, body, created_at, updated_at')
        .eq('restaurant_id', restaurantId)
        .eq('template_key', templateKey)
        .eq('language', language)
        .single();
      if (error && error.code !== 'PGRST116') { // PGRST116: no rows found
        throw new Error(error.message);
      }
      return data || null;
    },
    enabled: !!restaurantId && !!templateKey && !!language,
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ restaurantId, templateKey, language, subject, body }) => {
      if (!restaurantId || !templateKey || !language || !subject || !body) {
        throw new Error('All fields are required');
      }
      const { data: existingTemplate, error: fetchError } = await supabase
        .from('email_templates')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('template_key', templateKey)
        .eq('language', language)
        .single();
      if (fetchError && fetchError.code !== 'PGRST116') {
        throw new Error(fetchError.message);
      }
      let result;
      if (existingTemplate) {
        const { data, error } = await supabase
          .from('email_templates')
          .update({ subject, body, updated_at: new Date().toISOString() })
          .eq('id', existingTemplate.id)
          .select();
        if (error) {
          throw new Error(error.message);
        }
        result = data[0];
      } else {
        const { data, error } = await supabase
          .from('email_templates')
          .insert({ restaurant_id: restaurantId, template_key: templateKey, language, subject, body })
          .select();
        if (error) {
          throw new Error(error.message);
        }
        result = data[0];
      }
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['emailTemplates', variables.restaurantId]);
      queryClient.invalidateQueries(['emailTemplate', variables.restaurantId, variables.templateKey, variables.language]);
    },
    onError: (error) => {
      console.error('Error updating email template:', error);
    },
  });
};




export async function sendEmail({
  restaurantId,
  templateKey,
  language,
  recipient,
  placeholders = {},
}) {
  try {
    // Validate required parameters
    if (!restaurantId || !templateKey || !language || !recipient) {
      throw new Error('Missing required parameters: restaurantId, templateKey, language, or recipient');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipient)) {
      throw new Error('Invalid recipient email address');
    }

    // Fetch the email template from Supabase
    const { data: template, error: templateError } = await supabase
      .from('email_templates')
      .select('subject, body')
      .eq('restaurant_id', restaurantId)
      .eq('template_key', templateKey)
      .eq('language', language)
      .single();

    if (templateError || !template) {
      console.error('Error fetching template:', templateError);
      throw new Error(`Failed to fetch template: ${templateError?.message || 'Template not found'}`);
    }

    // Replace placeholders in the email body (handle [key] format)
    let finalHtml = template.body;
    Object.entries(placeholders).forEach(([key, value]) => {
      const placeholder = `\\[${key}\\]`;
      finalHtml = finalHtml.replace(new RegExp(placeholder, 'g'), String(value));
    });

    console.log('Sending email with:', {
      restaurantId,
      templateKey,
      language,
      recipient,
      subject: template.subject,
      finalHtml: finalHtml.slice(0, 100) + (finalHtml.length > 100 ? '...' : ''),
    });

    // Invoke the Supabase Edge Function
    const { data, error } = await supabaseFreeTable.functions.invoke('emailSender', {
      body: {
        email: recipient,
        subject: template.subject,
        body: finalHtml,
      },
    });

    if (error) {
      console.error('Error invoking emailSender:', error.message, error);
      throw new Error(`Failed to send email: ${error.message}`);
    }

    console.log('Email sent successfully:', data);
    return { success: true, data };

  } catch (error) {
    console.error('sendEmail error:', error.message, error);
    return { success: false, error: error.message };
  }
}